import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NovaTraderLogin,
  useNovaTraderLogin,
} from '@hub/components/nova-traders-login';
import { getTradingSystemName } from '@monorepo/client-types';
import { SystemIcon } from '@monorepo/nova-icons';
import { alpha, IconButton, Stack, Typography } from '@monorepo/nova-ui';
import { TradingSystem } from '@monorepo/types';
import {
  StyledDialog,
  StyledDialogContent,
  StyledDialogTitle,
  StyledTitleRow,
  StyledTitleTypography,
} from '../styled';

interface NovaCredentialsModalProps {
  isOpen: boolean;
  onClose: () => void;
  displayAccountName: string;
  userId: string;
  tradingSystem: TradingSystem;
  accountId: string;
  contest: boolean;
}

export const NovaCredentialsModal: React.FC<NovaCredentialsModalProps> = ({
  isOpen,
  onClose,
  displayAccountName,
  userId,
  tradingSystem,
  accountId,
  contest,
}) => {
  const { t } = useTranslation('login');
  const { showPassword, togglePasswordVisibility } = useNovaTraderLogin({
    userId,
    tradingSystem,
    accountId,
    contest,
  });

  const isNoCredentials =
    tradingSystem === TradingSystem.CTrader ||
    tradingSystem === TradingSystem.BlackArrowFutures;

  const tradingSystemName = getTradingSystemName(tradingSystem);

  const credentialsInfo = (
    <Stack
      mt={5}
      gap={4}
      direction="row"
      alignItems="center"
      justifyContent="center"
      sx={(theme) => ({
        backgroundColor: alpha(theme.palette.yellow.main, 0.1),
        border: `1px solid ${theme.palette.yellow.main}`,
        padding: theme.spacing(1.5, 3),
        borderRadius: theme.spacing(2),
        color: theme.palette.text.main,
      })}
    >
      <SystemIcon iconName="Info" />
      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
        {t('traders_login.asset_type_info', {
          tradingSystem: tradingSystemName,
        })}
      </Typography>
    </Stack>
  );

  return (
    <StyledDialog open={isOpen} onClose={onClose}>
      <StyledDialogTitle>
        <Stack gap={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <SystemIcon
                iconName="LockSimpleOpen"
                size="xsmall"
                color="primary"
              />
              <Typography
                variant="subtitle2"
                sx={(theme) => ({
                  color: theme.palette.primary.main,
                })}
              >
                {displayAccountName}
              </Typography>
            </Stack>
          </Stack>

          <StyledTitleRow>
            <StyledTitleTypography variant="h3">
              {t('traders_login.account_credentials')}
            </StyledTitleTypography>
            {!isNoCredentials && (
              <IconButton onClick={togglePasswordVisibility}>
                <SystemIcon
                  iconName={showPassword ? 'EyeSlash' : 'Eye'}
                  size="small"
                />
              </IconButton>
            )}
          </StyledTitleRow>
        </Stack>
      </StyledDialogTitle>
      <StyledDialogContent sx={{ mt: 2 }}>
        <Suspense fallback={null}>
          <NovaTraderLogin
            userId={userId}
            tradingSystem={tradingSystem}
            accountId={accountId}
            contest={contest}
            externalShowPassword={showPassword}
            externalTogglePasswordVisibility={togglePasswordVisibility}
          />
        </Suspense>
        {isNoCredentials && credentialsInfo}
      </StyledDialogContent>
    </StyledDialog>
  );
};
